import React, { useState, useEffect, useMemo } from 'react';
import {
  PhoneCall, Phone, Search, RefreshCw, AlertTriangle, CheckCircle2,
  XCircle, Clock, Package, FlaskConical, Workflow,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useUser } from '@/contexts/UserContext';
import { getScheduledCalls, ScheduledCall } from '@/actions/drip-campaigns';

/**
 * Calls a journey has asked an agent to make.
 *
 * Outcomes are never entered here — they are read off `crm_call_logs`, so a task clears
 * itself once the agent actually dials. That means the only thing distinguishing a task
 * someone is about to action from one nobody will ever touch is its **age**, and an
 * unactioned task holds its journey open indefinitely by design. Age is therefore the
 * primary visual signal on this screen, not a detail.
 */

const OUTCOME_STYLE: Record<string, { cls: string; icon: React.ReactNode; label: string }> = {
  connected: {
    cls: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    icon: <CheckCircle2 className="w-3 h-3" />, label: 'Connected',
  },
  no_answer: {
    cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
    icon: <XCircle className="w-3 h-3" />, label: 'No answer',
  },
  busy: {
    cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
    icon: <XCircle className="w-3 h-3" />, label: 'Busy',
  },
};

// Age thresholds. A task older than a working day has almost certainly been forgotten,
// and its journey has been stalled that whole time.
const AGE_WARN_HOURS = 8;
const AGE_STALE_HOURS = 24;

const ageStyle = (h: number) =>
  h >= AGE_STALE_HOURS
    ? 'text-red-600 dark:text-red-400 font-semibold'
    : h >= AGE_WARN_HOURS
      ? 'text-amber-600 dark:text-amber-400 font-medium'
      : 'text-gray-500 dark:text-gray-400';

const fmtAge = (h: number) => {
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h.toFixed(1)}h`;
  return `${Math.floor(h / 24)}d`;
};

const ScheduledCallsPage: React.FC = () => {
  const { userData } = useUser();
  const isSuperAdmin = userData?.data?.role?.role_id === 9401;

  const [calls, setCalls] = useState<ScheduledCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<'open' | 'done' | 'all'>('open');
  // Supervisors open on the team view. A journey routes a call to the lead's existing
  // owner, so an admin's own "Mine" list is legitimately empty while tasks are piling up
  // for their agents — landing there shows an all-clear that is not true.
  const [scope, setScope] = useState<'mine' | 'all'>(isSuperAdmin ? 'all' : 'mine');
  const [search, setSearch] = useState('');

  useEffect(() => { load(); }, [status, scope]);

  // The context may resolve after first render; adopt the team view once it does.
  useEffect(() => {
    if (isSuperAdmin) setScope('all');
  }, [isSuperAdmin]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getScheduledCalls({ status, scope });
      setCalls(res?.data || []);
    } catch (e: any) {
      console.error(e);
      toast.error(e?.response?.data?.message || 'Could not load scheduled calls');
    } finally {
      setLoading(false);
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return calls;
    return calls.filter((c) =>
      (c.lead_name || '').toLowerCase().includes(q) ||
      (c.lead_phone || '').includes(q) ||
      (c.product_title || '').toLowerCase().includes(q) ||
      (c.campaign_name || '').toLowerCase().includes(q) ||
      (c.order_name || '').toLowerCase().includes(q)
    );
  }, [calls, search]);

  const stale = useMemo(
    () => calls.filter((c) => !c.outcome && c.age_hours >= AGE_STALE_HOURS).length,
    [calls]
  );

  return (
    <div className="w-full">
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-brandColor/10">
            <PhoneCall className="w-6 h-6 text-brandColor" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              Journey Calls
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Calls your product journeys have asked for. Outcome is detected from your
              call logs automatically — just make the call.
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {stale > 0 && (
          <div className="mb-4 flex items-start gap-3 p-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/30">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-red-800 dark:text-red-200">
                {stale} call{stale === 1 ? '' : 's'} waiting over 24 hours
              </p>
              <p className="text-red-700 dark:text-red-300 mt-0.5">
                Each of these is holding its journey open — no later step runs until the
                call is made.
              </p>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-4">
          {(['open', 'done', 'all'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-3 py-1.5 text-sm rounded-lg capitalize ${
                status === s
                  ? 'bg-brandColor text-white'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              {s === 'open' ? 'To call' : s === 'done' ? 'Called' : 'All'}
            </button>
          ))}

          <div className="w-px h-6 bg-gray-200 dark:bg-gray-600 mx-1" />

          {/* The API forces non-super users to their own tasks regardless, so this is a
              convenience for supervisors rather than a permission boundary. */}
          {(['mine', 'all'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setScope(s)}
              className={`px-3 py-1.5 text-sm rounded-lg ${
                scope === s
                  ? 'bg-gray-800 text-white dark:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              {s === 'mine' ? 'Mine' : 'Whole team'}
            </button>
          ))}

          <div className="flex-1" />

          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, phone, product, order…"
              className="pl-9 pr-3 py-1.5 text-sm w-64 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor focus:border-transparent"
            />
          </div>
          <button
            onClick={load}
            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor" />
            <p className="mt-4 text-gray-500">Loading calls…</p>
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <Phone className="w-14 h-14 text-gray-300 mx-auto mb-3" />
            {search ? (
              <p className="text-gray-500 dark:text-gray-400">
                Nothing matches “{search}”.
              </p>
            ) : scope === 'mine' ? (
              <>
                {/* Never claim all-clear from a single person's list — a journey sends
                    the call to the lead's owner, who may well be someone else. */}
                <p className="text-gray-600 dark:text-gray-300">
                  No calls assigned to you
                  {status === 'open' ? ' right now' : ''}.
                </p>
                {/* Only offer the team view to someone who can actually see it — the API
                    forces everyone else back to their own tasks, so the button would
                    return the same empty list and then read as "nobody has any". */}
                {isSuperAdmin && (
                  <>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Journeys route each call to the lead's owner, so there may be calls
                      waiting for other agents.
                    </p>
                    <button
                      onClick={() => setScope('all')}
                      className="mt-4 px-4 py-2 text-sm rounded-lg bg-brandColor text-white hover:opacity-90"
                    >
                      Show the whole team
                    </button>
                  </>
                )}
              </>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">
                {status === 'open'
                  ? 'No calls waiting for anyone. Nice.'
                  : 'Nothing to show.'}
              </p>
            )}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-left">
                  <tr className="text-xs uppercase text-gray-500 dark:text-gray-400">
                    <th className="px-4 py-3 font-medium">Waiting</th>
                    <th className="px-4 py-3 font-medium">Customer</th>
                    <th className="px-4 py-3 font-medium">Product / Order</th>
                    <th className="px-4 py-3 font-medium">Journey step</th>
                    <th className="px-4 py-3 font-medium">Last remark</th>
                    <th className="px-4 py-3 font-medium">Outcome</th>
                    <th className="px-4 py-3 font-medium" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {visible.map((c) => {
                    const o = c.outcome ? OUTCOME_STYLE[c.outcome] : null;
                    return (
                      <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 ${ageStyle(c.age_hours)}`}>
                            <Clock className="w-3.5 h-3.5" />
                            {fmtAge(c.age_hours)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {c.lead_name || '—'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {c.lead_phone || 'no phone'}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
                            <Package className="w-3.5 h-3.5 text-gray-400" />
                            {c.product_title || '—'}
                          </div>
                          {c.order_name && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {c.order_name}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
                            <Workflow className="w-3.5 h-3.5 text-gray-400" />
                            {c.step_name}
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {c.campaign_name}
                            {c.test_mode && (
                              <span
                                className="inline-flex items-center gap-0.5 px-1.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300"
                                title="This journey is in test mode"
                              >
                                <FlaskConical className="w-2.5 h-2.5" /> test
                              </span>
                            )}
                          </div>
                          {c.note && (
                            <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 italic">
                              “{c.note}”
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                          <span className="text-gray-600 dark:text-gray-400 line-clamp-2">
                            {c.last_remark || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {o ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${o.cls}`}>
                              {o.icon} {o.label}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">Not called yet</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {c.lead_phone && (
                            <a
                              href={`tel:${c.lead_phone}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded bg-green-600 text-white hover:bg-green-700"
                            >
                              <Phone className="w-3.5 h-3.5" /> Call
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduledCallsPage;
